import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CardId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';

import { useOrgContext } from './use_org_context';
import { fireAndForget } from '@web/ui/core/utils';

export interface UseClaimCard {
  /** Open the confirmation dialog. */
  requestClaim: () => void;
  /** Confirm — dispatches `card.claim` and closes the dialog on success. */
  confirmClaim: () => void;
  /** Dismiss the dialog without claiming. */
  cancel: () => void;
  isOpen: boolean;
  pending: boolean;
}

/**
 * Shared "assume card" flow. Owns the confirm-dialog open state and the
 * `card.claim` mutation (invalidating the card detail + boards on success).
 * Consumed by the card-drawer header and the conversation banner so both
 * triggers route through the same confirmation + cascade.
 */
export function useClaimCard(cardId: CardId): UseClaimCard {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      boardRepository.dispatchCardEvent(orgId, cardId, { event: { type: 'card.claim' } }),
    onSuccess: () => {
      setIsOpen(false);
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(orgId, cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
      // The overview ("Você está atendendo") is its own read model.
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.overview.all }));
    },
  });

  return {
    requestClaim: () => {
      setIsOpen(true);
    },
    confirmClaim: () => {
      mutation.mutate();
    },
    cancel: () => {
      setIsOpen(false);
    },
    isOpen,
    pending: mutation.isPending,
  };
}
